import { Document } from "@models";
import { useEffect, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ROUTE_SLUGS, ROUTES } from "src/config/navigation/routes";
import { fillRouteWithSlugs } from "src/utils/navigation";
import { useGetDocuments } from "../hooks";

type UseQmsLegacyRouteResult = {
  resolvedDocId?: string;
  documents?: Document[];
  isLoadingDocuments: boolean;
  isRerouting: boolean;
};

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const DOCUMENT_DELETION_POLL_INTERVAL_MS = 10_000;

export const useQmsLegacyRoute = (): UseQmsLegacyRouteResult => {
  const { deviceId = "", docId: routeDocSegment, orgId = "" } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const legacyDocId = deviceId ? searchParams.get("d") || undefined : undefined;
  const isCanonicalDocIdSegment = routeDocSegment
    ? UUID_PATTERN.test(routeDocSegment)
    : false;

  const { data: documents, isPending: isLoadingDocuments } = useGetDocuments(
    orgId,
    deviceId,
    {
      queryKeyOrgId: deviceId ? undefined : orgId,
      refetchInterval:
        routeDocSegment || legacyDocId
          ? DOCUMENT_DELETION_POLL_INTERVAL_MS
          : false,
    }
  );

  const selectedDocumentById = useMemo(
    () =>
      routeDocSegment
        ? documents?.find((document) => document.id === routeDocSegment)
        : undefined,
    [documents, routeDocSegment]
  );

  const templateKeyMatches = useMemo(
    () =>
      routeDocSegment
        ? documents?.filter((document) => document.templateKey === routeDocSegment) ??
          []
        : [],
    [documents, routeDocSegment]
  );

  const resolvedDocId =
    legacyDocId ??
    (!deviceId || isCanonicalDocIdSegment ? routeDocSegment : undefined) ??
    selectedDocumentById?.id ??
    (templateKeyMatches.length === 1 ? templateKeyMatches[0].id : undefined);

  const buildQmsUrl = (docId?: string, params?: URLSearchParams) => {
    const pathname = docId
      ? fillRouteWithSlugs(ROUTES.QMS_OPEN_DOC, {
          [ROUTE_SLUGS.ORG_ID]: orgId,
          [ROUTE_SLUGS.DEVICE_ID]: deviceId,
          [ROUTE_SLUGS.DOC_ID]: docId,
        })
      : fillRouteWithSlugs(ROUTES.QMS, {
          [ROUTE_SLUGS.ORG_ID]: orgId,
          [ROUTE_SLUGS.DEVICE_ID]: deviceId,
        });
    const query = params?.toString();
    return query ? `${pathname}?${query}` : pathname;
  };

  const isTemplateRoute =
    !!deviceId && !!routeDocSegment && !isCanonicalDocIdSegment;
  const isRerouting =
    !!legacyDocId ||
    (isTemplateRoute &&
      (documents === undefined || !selectedDocumentById));

  useEffect(() => {
    if (!deviceId || !routeDocSegment) return;

    if (legacyDocId) {
      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.delete("d");
      navigate(buildQmsUrl(legacyDocId, nextSearchParams), { replace: true });
      return;
    }

    if (!documents || isCanonicalDocIdSegment || selectedDocumentById) return;

    if (templateKeyMatches.length === 1) {
      navigate(buildQmsUrl(templateKeyMatches[0].id, searchParams), {
        replace: true,
      });
      return;
    }

    navigate(buildQmsUrl(undefined, searchParams), { replace: true });
  }, [
    deviceId,
    documents,
    isCanonicalDocIdSegment,
    legacyDocId,
    navigate,
    routeDocSegment,
    searchParams,
    selectedDocumentById,
    templateKeyMatches,
  ]);

  return {
    resolvedDocId,
    documents,
    isLoadingDocuments,
    isRerouting,
  };
};
